/**
 * **The to-hit lane's events** (`event-engine-to-hit`, `decisions/0212` item 1): a roll of dice, an
 * attack attempt, its outcome, and a save or check. Each is per caught target and caused, through
 * the attempt, by the action's `action_declared`.
 *
 *   - {@link RollEvent}: one draw of dice, for any purpose. Its `spec` says what is drawn; "happens"
 *     draws it and fills `faces`. A roll grounds no step: its faces reach the record through the
 *     event that consumes it (the `to_hit`, `save`, `damage` or `table_check` step).
 *   - {@link AttackAttemptEvent}: an attacker tries a target. Unforced, it raises the attack roll;
 *     forced (a declared miss, an Autopod's `autoHit`), it goes straight to the outcome.
 *   - {@link AttackOutcomeEvent}: hit, crit or miss. "Happens" grounds the `to_hit` step once, from
 *     the final values (or the `miss_chance` step on an Invisible coin miss).
 *   - {@link CheckEvent}: a save, a check or the overheating pool, settled against its DC.
 *
 * Draw order is a contract (scripted proof dice and recorded logs depend on it), so raising these
 * events never moves a draw: {@link drawRoll} draws exactly what the site drew before.
 */
import type { DiceSource } from "../../dice";
import type { Cause, EventLoop, KernelEvent, Replacement } from "./loop";

/** What a roll is for. */
export type RollPurpose =
  | { kind: "attack"; attacker: string; target: string; tech: boolean }
  | { kind: "save" | "check"; stat: string; cause?: string }
  | { kind: "overheating" }
  /** A damage instance's dice (`instance` 0 is the primary). A shared pool is an area attack's, drawn once with its crit extension. */
  | { kind: "damage"; instance: number; crit: boolean; shared?: true }
  | { kind: "miss_chance"; target: string };

/**
 * What a roll draws: each `dice` pool in order, keeping the `keepHighest` best of the first pool
 * when set, then `|accuracy|` d6 (the best one added for accuracy, subtracted for difficulty).
 * `maximize` draws nothing and shows every kept die's top face; `rerollOnes` (Overkill) rerolls each
 * die that shows a 1 until it doesn't.
 */
export type RollSpec = {
  dice: { count: number; sides: number }[];
  keepHighest?: number;
  accuracy: number;
  maximize?: true;
  rerollOnes?: true;
};

/** One draw of dice. `faces`, `accuracyMod` and `overkillHeat` are filled when it happens. */
export type RollEvent = {
  kind: "roll";
  roller: string;
  purpose: RollPurpose;
  spec: RollSpec;
  /** Every face drawn, in draw order. */
  faces?: number[];
  /** The accuracy/difficulty pool's signed contribution, on an attack or save roll. */
  accuracyMod?: number;
  /** Heat Overkill's rerolls owe the roller. */
  overkillHeat?: number;
};

/** An attack forced to an outcome without a d20: a declared reaction's miss (`by` is the reactor), or an `autoHit`. */
export type AttackForced = { verdict: "miss"; by: string } | { verdict: "hit" };

/**
 * An attacker tries a target. `coin` is Invisible's pre-roll coin, when a miss-chance replacement
 * drew one: face 1 is the miss face, and the attack misses outright, with no d20.
 */
export type AttackAttemptEvent = { kind: "attack_attempt"; attacker: string; target: string; forced?: AttackForced; coin?: { face: number; chance: number } };

/**
 * An attack's hit, crit or miss. `roll` is the id of the attack roll that stands (a reroll's, when
 * one was taken); absent when nothing was rolled (forced, or Invisible's coin). `resistant` grants
 * the target Resistance to the attack's damage (Hunker Down, a declared `resist_pending_damage`).
 */
export type AttackOutcomeEvent = {
  kind: "attack_outcome";
  attacker: string;
  target: string;
  verdict: "hit" | "crit" | "miss";
  roll?: string;
  /** The standing roll's kept d20 face; 0 when nothing was rolled. */
  d20: number;
  natural20: boolean;
  total: number;
  forced?: AttackForced;
  resistant?: true;
  /** Invisible's coin made the attack miss outright: the face drawn and the chance it carried. */
  missChance?: { coin: number; chance: number };
  /** The replacement that turned a hit into a miss (Barrel Roll), so a miss-side reroll knows the miss wasn't rolled. */
  missedBy?: string;
};

/**
 * A save, a check or the overheating pool, settled. `roll` is the id of the roll that stands (absent
 * when the roll drew with no loop). A save or check carries its `dc` and `verdict`; the overheating
 * pool carries neither (its `total` is the lowest face, which picks the table row).
 */
export type CheckEvent = {
  kind: "check";
  roller: string;
  purpose: Extract<RollPurpose, { kind: "save" | "check" | "overheating" }>;
  roll?: string;
  total: number;
  dc?: number;
  verdict?: "pass" | "fail";
};

/**
 * **A declared reaction made the attack on `target` miss** (`event-engine-to-hit` R11): a `cause_miss`
 * atom in a declared-phase reaction (Interdiction Point's contested roll won), raised under the
 * declaration. The attack's attempt on `target` reads it as a forced miss, with no d20.
 */
export type AttackForcedMissEvent = { kind: "attack_forced_miss"; target: string; by: string };

/** **A declared reaction gave `target` Resistance to the attack's damage** (a `resist_pending_damage` in a declared-phase reaction). The outcome reads it. */
export type AttackResistedEvent = { kind: "attack_resisted"; target: string };

/** Named args for {@link drawRoll}. */
export type DrawRollArgs<R> = {
  /** The intent's loop. Absent (a direct table fixture, a probe), the site draws with no event. */
  loop: EventLoop | undefined;
  /** The site's dice source: the loop's own wherever the loop is the intent's. */
  dice: DiceSource;
  roll: RollEvent;
  /** The replacements on this roll (advantage, maximize), in F2a order. They rewrite its `spec` before anything is drawn. */
  replacements?: readonly Replacement[];
  /** The site's draw, through the dice it is handed, of the roll as the replacements left it. */
  draw: (dice: DiceSource, roll: RollEvent) => R;
  /** The facts the draw settled that the event carries besides its faces. */
  facts?: (result: R) => Pick<RollEvent, "accuracyMod" | "overkillHeat">;
  cause?: Cause;
};

/**
 * Raise a {@link RollEvent} and draw it: "happens" runs the site's own `draw` and fills the event's
 * faces from the dice it took. Returns the draw's result and the landed roll's id (absent with no loop).
 */
export function drawRoll<R>(args: DrawRollArgs<R>): { result: R; id?: string } {
  const { loop, dice, roll, replacements = [], draw, facts, cause } = args;
  if (loop === undefined) return { result: draw(dice, proposeRoll(roll, replacements)) };
  let drawn: { result: R } | undefined;
  const id = loop.raise(
    roll,
    {
      replacements,
      happen: (event) => {
        if (event.kind !== "roll") throw new Error(`drawRoll: a replacement turned a roll into ${event.kind}`);
        const faces: number[] = [];
        const result = draw({ roll: (sides) => { const v = dice.roll(sides); faces.push(v); return v; } }, event);
        drawn = { result };
        Object.assign(event, { faces }, facts?.(result));
      },
      trigger: (h) => h.id,
    },
    cause,
  );
  if (drawn === undefined) throw new Error("drawRoll: a replacement cancelled a roll, which nothing may do yet");
  return { result: drawn.result, ...(id !== undefined ? { id } : {}) };
}

/**
 * The roll the replacements leave, without drawing it: each rewrite in order, as the loop applies
 * them. The forecast reads a roll's shape from here (advantage), and a roll raised with no loop
 * draws from here.
 */
export function proposeRoll(roll: RollEvent, replacements: readonly Replacement[]): RollEvent {
  let event: KernelEvent = roll;
  for (const r of replacements) {
    const out = r.replace(event);
    if (out === null) throw new Error(`proposeRoll: ${r.id} cancelled a roll, which nothing may do yet`);
    event = "kind" in out ? out : out.event;
  }
  if (event.kind !== "roll") throw new Error(`proposeRoll: a replacement turned a roll into ${event.kind}`);
  return event;
}
